import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Alert,
    CircularProgress,
    FormHelperText,
    IconButton,
    Collapse,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { validateBr } from 'js-brasil';
import { maskCNPJ, maskCEP, maskPhone } from '../../utils/mask';
import isValidEmail from '../../utils/isValidEmail';
import AppNav from '../../components/appNav';
import { api } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const affiliateValidationSchema = yup.object({
    nomeEmpresa: yup
        .string()
        .min(4, 'Nome da empresa deve ter pelo menos 4 caracteres')
        .required('Nome da empresa é obrigatório'),
    presidente: yup
        .string()
        .required('Nome do presidente é obrigatório'),
    cnpj: yup
        .string()
        .test('cnpj', 'CNPJ inválido', value => validateBr.cnpj(value || ''))
        .required('CNPJ é obrigatório'),
    endereco: yup
        .string()
        .required('Endereço é obrigatório'),
    cidade: yup
        .string()
        .required('Cidade é obrigatória'),
    uf: yup
        .string()
        .required('UF é obrigatório'),
    cep: yup
        .string()
        .matches(/^\d{5}-?\d{3}$/, 'CEP inválido')
        .required('CEP é obrigatório'),
    telefone: yup
        .string()
        .min(14, 'Telefone inválido')
        .required('Telefone é obrigatório'),
    whatsapp: yup
        .string()
        .min(14, 'WhatsApp inválido')
        .required('WhatsApp é obrigatório'),
    email: yup
        .string()
        .test('email', 'Email inválido', value => !value || isValidEmail(value))
        .required('Email é obrigatório'),
    tipoRPPS: yup
        .string()
        .required('Tipo de RPPS é obrigatório'),
    tipoFiliado: yup
        .number()
        .min(1, 'Selecione o tipo de filiado')
        .required('Tipo de filiado é obrigatório')
});

const Affiliate = () => {
    const [loading, setLoading] = useState(false);
    const [loadingCep, setLoadingCep] = useState(false);
    const [loadingCnpj, setLoadingCnpj] = useState(false);
    const [loadingBoleto, setLoadingBoleto] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });
    const [boletoUrl, setBoletoUrl] = useState(null);
    const [showForm, setShowForm] = useState(true);
    const [cnpjsRelacionados, setCnpjsRelacionados] = useState([]);
    const [expandedRelacionados, setExpandedRelacionados] = useState({});
    const [ufError, setUfError] = useState('');
    const [openModalSegundaVia, setOpenModalSegundaVia] = useState(false);
    const [cnpjSegundaVia, setCnpjSegundaVia] = useState('');
    const [errorCnpjSegundaVia, setErrorCnpjSegundaVia] = useState('');
    const [tipoSegundaVia, setTipoSegundaVia] = useState('boleto');
    const navigate = useNavigate();
    const pendingTipoRPPS = useRef('');

    const tiposRelacionamento = [
        { value: 'PREFEITURA', label: 'Prefeitura' },
        { value: 'CAMARA', label: 'Câmara' },
        { value: 'AUTARQUIA_1', label: 'Autarquia 1' },
        { value: 'AUTARQUIA_2', label: 'Autarquia 2' }
    ];

    const tiposFiliado = [
        { id: 1, descricao: 'Até 1.000 beneficiários', valor: 955.00 },
        { id: 2, descricao: 'De 1.001 até 3.000 beneficiários', valor: 1185.00 },
        { id: 3, descricao: 'De 3.001 a 5.000 beneficiários', valor: 2105.00 },
        { id: 4, descricao: 'Acima de 5.001 beneficiários', valor: 3025.00 }
    ];

    const estadosBrasileiros = [
        'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
        'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
        'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
    ];

    const formik = useFormik({
        initialValues: {
            nomeEmpresa: '',
            presidente: '',
            cnpj: '',
            endereco: '',
            cidade: '',
            uf: '',
            cep: '',
            telefone: '',
            whatsapp: '',
            reference: `Anuidade ${new Date().getFullYear()}`,
            email: '',
            tipoRPPS: '',
            tipoFiliado: ''
        },
        validationSchema: affiliateValidationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            setAlert({ show: false, type: 'success', message: '' });

            // Validar CNPJs relacionados antes de enviar
            const cnpjsInvalidos = validarTodosCnpjsRelacionados();
            if (cnpjsInvalidos.length > 0) {
                setLoading(false);
                setAlert({
                    show: true,
                    type: 'error',
                    message: `Corrija os erros nas instituições relacionadas antes de enviar o formulário.`
                });
                return;
            }

            try {
                const tipoSelecionado = tiposFiliado.find(t => t.id === parseInt(values.tipoFiliado));

                const dataToSend = {
                    ...values,
                    descricaoTipoFiliado: tipoSelecionado?.descricao || '',
                    valorAnual: tipoSelecionado?.valor || 0,
                    instituicoesRelacionadas: cnpjsRelacionados.map(rel => ({
                        cnpj: rel.cnpj,
                        tipo: rel.tipo,
                        nomeEmpresa: rel.nomeEmpresa,
                        endereco: rel.endereco,
                        cidade: rel.cidade,
                        uf: rel.uf,
                        cep: rel.cep,
                        telefone: rel.telefone,
                        email: rel.email
                    }))
                };

                // Enviar para a API
                const response = await api.post('/affiliate-payments', { data: dataToSend });
                console.log('Resposta da API:', response.data);

                if (response.data) {
                    const affiliate = response.data;

                    setAlert({
                        show: true,
                        type: 'success',
                        message: affiliate.message || 'Filiação registrada com sucesso!'
                    });

                    // Se houver boleto, salvar URL e ocultar formulário
                    if (affiliate.boletoUrl) {
                        setBoletoUrl(affiliate.boletoUrl);
                        setShowForm(false);
                    }
                } else {
                    throw new Error('Resposta inválida do servidor');
                }

            } catch (error) {
                console.error('Erro ao enviar filiação:', error);
                setAlert({
                    show: true,
                    type: 'error',
                    message: error.response?.data?.error?.message || 'Erro ao enviar solicitação de filiação'
                });
            } finally {
                setLoading(false);
            }
        }
    });

    const parseRelacionadas = (raw) => {
        try {
            let parsed = raw;
            while (typeof parsed === 'string') parsed = JSON.parse(parsed);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    };

    const loadRelacionadas = async (lista) => {
        const itens = await Promise.all(lista.map(async (rel, index) => {
            let company = {};
            try {
                const response = await api.get(`/companies/${String(rel.cnpj).replace(/\//g, '%2F')}`);
                company = response.data || {};
            } catch (e) {
                console.log('Instituição relacionada não encontrada');
            }
            return {
                id: Date.now() + index,
                cnpj: rel.cnpj || '',
                tipo: rel.tipo || '',
                nomeEmpresa: company.social_reason || rel.nomeEmpresa || '',
                endereco: company.address || '',
                cidade: company.city || '',
                uf: company.state || '',
                cep: company.cep ? maskCEP(company.cep) : '',
                telefone: company.phone ? maskPhone(company.phone) : '',
                email: company.email || '',
                loading: false,
                ufPreenchidoAutomaticamente: !!company.state,
                errors: {}
            };
        }));
        setCnpjsRelacionados(itens);
        setExpandedRelacionados(Object.fromEntries(itens.map(i => [i.id, false])));
    };

    const handleCnpjBlur = async (cnpj) => {
        setLoadingCnpj(true);
        const cnpjEncoded = cnpj.replace(/\//g, '%2F');
        const blank = {
            nomeEmpresa: '', presidente: '', endereco: '', cidade: '', uf: '', cep: '',
            telefone: '', whatsapp: '', email: '', tipoRPPS: '', tipoFiliado: ''
        };
        try {
            const response = await api.get(`/companies/${cnpjEncoded}`);
            const company = response.data;
            if (!company) throw new Error('not found');

            const affiliate = company.affiliate || null;
            const tipoRPPS = affiliate && ['RPPS_ASSOCIADO', 'RPPS_OUTROS_ESTADOS'].includes(affiliate.type)
                ? affiliate.type : '';
            const novoUf = company.state || '';
            if (novoUf && novoUf !== formik.values.uf) {
                // o efeito de mudança de UF limpa o tipo; reaplica depois dele
                pendingTipoRPPS.current = tipoRPPS;
            }

            formik.setValues({
                ...formik.values,
                ...blank,
                cnpj: maskCNPJ(cnpj),
                nomeEmpresa: company.social_reason || '',
                presidente: company.president || '',
                endereco: company.address || '',
                cidade: company.city || '',
                uf: novoUf,
                cep: company.cep ? maskCEP(company.cep) : '',
                telefone: company.phone ? maskPhone(company.phone) : '',
                whatsapp: company.whatsapp ? maskPhone(company.whatsapp) : '',
                email: company.email || '',
                tipoRPPS: novoUf && novoUf !== formik.values.uf ? '' : tipoRPPS
            });

            const relacionadas = affiliate ? parseRelacionadas(affiliate.instituicoesRelacionadas) : [];
            if (relacionadas.length > 0) {
                await loadRelacionadas(relacionadas);
            } else {
                setCnpjsRelacionados([]);
            }

            setAlert({ show: true, type: 'success', message: 'Dados da empresa carregados com sucesso!' });
            setTimeout(() => setAlert({ show: false, type: 'success', message: '' }), 3000);
        } catch (error) {
            console.log('Empresa não encontrada, preencha manualmente');
            pendingTipoRPPS.current = '';
            formik.setValues({ ...formik.values, ...blank, cnpj: maskCNPJ(cnpj) });
            setCnpjsRelacionados([]);
        } finally {
            setLoadingCnpj(false);
        }
    };
    const buscarReciboPagamento = async () => {
        const cnpj = cnpjSegundaVia;

        if (!cnpj || !validateBr.cnpj(cnpj)) {
            setErrorCnpjSegundaVia('CNPJ inválido');
            return;
        }

        setLoadingBoleto(true);
        setErrorCnpjSegundaVia('');

        try {
            const cnpjEncoded = cnpj.replace(/\//g, '%2F');
            const response = await api.get(`/affiliate-payments/${cnpjEncoded}`);
            if (response.data && response.data.external_id ) {
                if(response.data.status !== 'PAGO'){
                    setErrorCnpjSegundaVia('Boleto pendente encontrado para este CNPJ.');
                    return 
                }
                navigate('/recibo/' + response.data.external_id);
                setAlert({
                    show: true,
                    type: 'success',
                    message: 'Boleto pendente encontrado!'
                });
                handleCloseModalSegundaVia();
            }
        } catch (error) {
            console.error('Erro ao buscar boleto pendente:', error);
            setErrorCnpjSegundaVia(
                error.response?.data?.error?.message ||
                'Erro ao buscar boleto pendente. Nenhum boleto encontrado para este CNPJ.'
            );
        } finally {

            setLoadingBoleto(false);
        }
    };

    const buscarBoletoPendente = async () => {
        const cnpj = cnpjSegundaVia;

        if (!cnpj || !validateBr.cnpj(cnpj)) {
            setErrorCnpjSegundaVia('CNPJ inválido');
            return;
        }

        setLoadingBoleto(true);
        setErrorCnpjSegundaVia('');

        try {
            const cnpjEncoded = cnpj.replace(/\//g, '%2F');
            const response = await api.get(`/affiliate-payments/${cnpjEncoded}`);

            if (response.data && response.data.payment_url) {
                window.open(response.data.payment_url, '_blank');
                setAlert({
                    show: true,
                    type: 'success',
                    message: 'Boleto pendente encontrado!'
                });
                handleCloseModalSegundaVia();
            } else {
                setErrorCnpjSegundaVia('Nenhum boleto pendente encontrado para este CNPJ.');
            }
        } catch (error) {
            console.error('Erro ao buscar boleto pendente:', error);
            setErrorCnpjSegundaVia(
                error.response?.data?.error?.message ||
                'Erro ao buscar boleto pendente. Nenhum boleto encontrado para este CNPJ.'
            );
        } finally {
            setLoadingBoleto(false);
        }
    };

    const handleOpenModalSegundaVia = ({type}) => {
        console.log(type);
        setTipoSegundaVia(type);
        setOpenModalSegundaVia(true);
        setCnpjSegundaVia('');
        setErrorCnpjSegundaVia('');
    };

    const handleCloseModalSegundaVia = () => {
        setOpenModalSegundaVia(false);
        setCnpjSegundaVia('');
        setErrorCnpjSegundaVia('');
    };

    const handleCepBlur = async () => {
        const cep = formik.values.cep.replace(/\D/g, '');

        if (cep.length === 8) {
            setLoadingCep(true);
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
                const data = await response.json();

                if (!data.erro) {
                    formik.setFieldValue('endereco', data.logradouro);
                    formik.setFieldValue('cidade', data.localidade);
                    formik.setFieldValue('uf', data.uf);
                } else {
                    setAlert({ show: true, type: 'warning', message: 'CEP não encontrado' });
                }
            } catch (error) {
                console.error('Erro ao buscar CEP:', error);
            } finally {
                setLoadingCep(false);
            }
        }
    };

    const getTipoRPPSOptions = () => {
        if (formik.values.uf === 'PR') {
            return [{ value: 'RPPS_ASSOCIADO', label: 'RPPS Associado' }];
        } else if (formik.values.uf && formik.values.uf !== 'PR') {
            return [{ value: 'RPPS_OUTROS_ESTADOS', label: 'RPPS de Outros Estados' }];
        }
        return [];
    };

    const adicionarCnpjRelacionado = () => {
        const novoId = Date.now();
        setCnpjsRelacionados([...cnpjsRelacionados, {
            id: novoId,
            cnpj: '',
            tipo: '',
            nomeEmpresa: '',
            endereco: '',
            cidade: '',
            uf: '',
            cep: '',
            telefone: '',
            email: '',
            loading: false,
            ufPreenchidoAutomaticamente: false,
            errors: {}
        }]);
        setExpandedRelacionados({ ...expandedRelacionados, [novoId]: true });
    };

    const removerCnpjRelacionado = (id) => {
        setCnpjsRelacionados(cnpjsRelacionados.filter(cnpj => cnpj.id !== id));
        const { [id]: removed, ...rest } = expandedRelacionados;
        setExpandedRelacionados(rest);
    };

    const toggleExpandRelacionado = (id) => {
        setExpandedRelacionados({
            ...expandedRelacionados,
            [id]: !expandedRelacionados[id]
        });
    };

    const buscarDadosCNPJ = async (cnpj, id) => {
        const cnpjLimpo = cnpj.replace(/\D/g, '');
        if (cnpjLimpo.length !== 14) return;

        // Atualiza loading do item específico
        setCnpjsRelacionados(prev => prev.map(item =>
            item.id === id ? { ...item, loading: true } : item
        ));

        try {
            // Tenta buscar na API interna primeiro
            const cnpjEncoded = cnpj.replace(/\//g, '%2F');
            const response = await api.get(`/companies/${cnpjEncoded}`);

            if (response.data) {
                const company = response.data;
                setCnpjsRelacionados(prev => prev.map(item =>
                    item.id === id ? {
                        ...item,
                        nomeEmpresa: company.social_reason || '',
                        endereco: company.address || '',
                        cidade: company.city || '',
                        uf: company.state || '',
                        cep: company.cep ? maskCEP(company.cep) : '',
                        telefone: company.phone ? maskPhone(company.phone) : '',
                        email: company.email || '',
                        loading: false,
                        ufPreenchidoAutomaticamente: !!company.state
                    } : item
                ));
                return;
            }
        } catch (error) {
            // Se não encontrar na API interna, busca na BrasilAPI
            try {
                const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`);
                const data = await response.json();

                if (data && data.razao_social) {
                    setCnpjsRelacionados(prev => prev.map(item =>
                        item.id === id ? {
                            ...item,
                            nomeEmpresa: data.razao_social || '',
                            endereco: data.logradouro || '',
                            cidade: data.municipio || '',
                            uf: data.uf || '',
                            cep: data.cep ? maskCEP(data.cep) : '',
                            telefone: data.ddd_telefone_1 ? maskPhone(data.ddd_telefone_1) : '',
                            email: data.email || '',
                            loading: false,
                            ufPreenchidoAutomaticamente: !!data.uf
                        } : item
                    ));
                }
            } catch (brasilApiError) {
                console.log('Erro ao buscar CNPJ na BrasilAPI:', brasilApiError);
                setCnpjsRelacionados(prev => prev.map(item =>
                    item.id === id ? { ...item, loading: false } : item
                ));
            }
        }
    };

    const atualizarCnpjRelacionado = (id, field, value) => {
        setCnpjsRelacionados(prev => prev.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));

        // Validar UF se o campo alterado for UF
        if (field === 'uf') {
            validarUfRelacionados(id, value);
        }

        // Validar campo após atualização
        validarCampoRelacionado(id, field, value);
    };

    const validarCampoRelacionado = (id, field, value) => {
        let error = '';

        switch (field) {
            case 'cnpj':
                if (value && !validateBr.cnpj(value)) {
                    error = 'CNPJ inválido';
                }
                break;
            case 'nomeEmpresa':
                if (value && value.length < 4) {
                    error = 'Nome deve ter pelo menos 4 caracteres';
                }
                break;
            case 'cep':
                if (value && !/^\d{5}-?\d{3}$/.test(value)) {
                    error = 'CEP inválido';
                }
                break;
            case 'telefone':
                if (value && value.length < 14) {
                    error = 'Telefone inválido';
                }
                break;
            case 'email':
                if (value && !isValidEmail(value)) {
                    error = 'Email inválido';
                }
                break;
            default:
                break;
        }

        setCnpjsRelacionados(prev => prev.map(item =>
            item.id === id ? {
                ...item,
                errors: { ...item.errors, [field]: error }
            } : item
        ));
    };

    const validarTodosCnpjsRelacionados = () => {
        const cnpjsComErro = [];

        cnpjsRelacionados.forEach((relacionado, index) => {
            const erros = {};

            // Validar campos obrigatórios
            if (!relacionado.tipo) {
                erros.tipo = 'Tipo é obrigatório';
            }

            if (!relacionado.cnpj) {
                erros.cnpj = 'CNPJ é obrigatório';
            } else if (!validateBr.cnpj(relacionado.cnpj)) {
                erros.cnpj = 'CNPJ inválido';
            }

            if (!relacionado.nomeEmpresa) {
                erros.nomeEmpresa = 'Nome da empresa é obrigatório';
            } else if (relacionado.nomeEmpresa.length < 4) {
                erros.nomeEmpresa = 'Nome deve ter pelo menos 4 caracteres';
            }

            if (!relacionado.endereco) {
                erros.endereco = 'Endereço é obrigatório';
            }

            if (!relacionado.cidade) {
                erros.cidade = 'Cidade é obrigatória';
            }

            if (!relacionado.uf) {
                erros.uf = 'UF é obrigatório';
            } else if (relacionado.uf !== formik.values.uf) {
                erros.uf = `Deve ser ${formik.values.uf}`;
            }

            if (!relacionado.cep) {
                erros.cep = 'CEP é obrigatório';
            } else if (!/^\d{5}-?\d{3}$/.test(relacionado.cep)) {
                erros.cep = 'CEP inválido';
            }

            if (!relacionado.telefone) {
                erros.telefone = 'Telefone é obrigatório';
            } else if (relacionado.telefone.length < 14) {
                erros.telefone = 'Telefone inválido';
            }

            if (!relacionado.email) {
                erros.email = 'Email é obrigatório';
            } else if (!isValidEmail(relacionado.email)) {
                erros.email = 'Email inválido';
            }

            // Se houver erros, atualizar estado e adicionar à lista
            if (Object.keys(erros).length > 0) {
                setCnpjsRelacionados(prev => prev.map(item =>
                    item.id === relacionado.id ? { ...item, errors: erros } : item
                ));
                cnpjsComErro.push({ index: index + 1, erros });
            }
        });

        return cnpjsComErro;
    };

    const validarUfRelacionados = (idAtualizado = null, ufAtualizado = null) => {
        const ufPrincipal = formik.values.uf;

        if (!ufPrincipal) {
            setUfError('');
            return;
        }

        // Verificar se algum CNPJ relacionado tem UF diferente do principal
        const cnpjsComUfDiferente = cnpjsRelacionados.filter(cnpj => {
            if (idAtualizado && cnpj.id === idAtualizado) {
                return ufAtualizado && ufAtualizado !== ufPrincipal;
            }
            return cnpj.uf && cnpj.uf !== ufPrincipal;
        });

        if (cnpjsComUfDiferente.length > 0) {
            setUfError(`Todas as instituições relacionadas devem ter o mesmo UF do CNPJ principal (${ufPrincipal})`);
        } else {
            setUfError('');
        }
    };

    const getValorAnual = () => {
        const tipo = tiposFiliado.find(t => t.id === parseInt(formik.values.tipoFiliado));
        return tipo?.valor || 0;
    };

    React.useEffect(() => {
        if (formik.values.uf) {
            formik.setFieldValue('tipoRPPS', pendingTipoRPPS.current);
            pendingTipoRPPS.current = '';
            // Revalidar UFs dos relacionados quando UF principal mudar
            validarUfRelacionados();
        }
    }, [formik.values.uf]);

    React.useEffect(() => {
        // Revalidar sempre que a lista de CNPJs relacionados mudar
        if (formik.values.uf && cnpjsRelacionados.length > 0) {
            validarUfRelacionados();
        }
    }, [cnpjsRelacionados]);

    return (
        <Box sx={{ width: "100vw", minHeight: "100vh", bgcolor: 'background.default' }}>
            <AppNav />

            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, mt: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h4" sx={{ color: '#1D385E', fontWeight: 600 }}>
                            Formulário de Renovação e Filiação
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                onClick={() => handleOpenModalSegundaVia({ type: 'boleto' })}
                                color="success"
                            >
                                2ª Via do Boleto
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => handleOpenModalSegundaVia({ type: 'recibo' })}
                                color="warning"
                            >
                                Recibo
                            </Button>
                            <Button
                                component="a"
                                href="https://apeprev.sempereventos.com.br/uploads/Carta_Convite_Filiacao_470319c71b.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outlined"
                                startIcon={<PictureAsPdfIcon />}
                            >
                                Carta Convite
                            </Button>
                        </Box>
                    </Box>

                    {alert.show && (
                        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert({ ...alert, show: false })}>
                            {alert.message}
                        </Alert>
                    )}

                    {ufError && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setUfError('')}>
                            {ufError}
                        </Alert>
                    )}

                    {!showForm && boletoUrl && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" sx={{ mb: 3, color: '#1D385E' }}>
                                Sua filiação foi registrada com sucesso!
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => window.open(boletoUrl, '_blank')}
                                sx={{
                                    bgcolor: '#1D385E',
                                    '&:hover': { bgcolor: '#152a47' },
                                    mb: 2
                                }}
                            >
                                Visualizar Boleto
                            </Button>
                            <br />
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setShowForm(true);
                                    setBoletoUrl(null);
                                    formik.resetForm();
                                    setCnpjsRelacionados([]);
                                    setAlert({ show: false, type: 'success', message: '' });
                                }}
                                sx={{ mt: 2 }}
                            >
                                Nova Filiação
                            </Button>
                        </Box>
                    )}

                    {showForm && <form onSubmit={formik.handleSubmit}>
                        {/* Dados da Empresa */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2, color: '#1D385E' }}>
                            Dados da Empresa
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="CNPJ"
                                    name="cnpj"
                                    value={formik.values.cnpj}
                                    onChange={(e) => {
                                        const maskedValue = maskCNPJ(e.target.value);
                                        formik.setFieldValue('cnpj', maskedValue);

                                        if (validateBr.cnpj(maskedValue)) {
                                            handleCnpjBlur(e.target.value);
                                        }
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.cnpj && Boolean(formik.errors.cnpj)}
                                    helperText={formik.touched.cnpj && formik.errors.cnpj}
                                    InputProps={{
                                        endAdornment: loadingCnpj ? <CircularProgress size={20} /> : null
                                    }}
                                    inputProps={{ maxLength: 18 }}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    fullWidth
                                    label="Nome da Empresa"
                                    name="nomeEmpresa"
                                    value={formik.values.nomeEmpresa}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.nomeEmpresa && Boolean(formik.errors.nomeEmpresa)}
                                    helperText={formik.touched.nomeEmpresa && formik.errors.nomeEmpresa}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Presidente"
                                    name="presidente"
                                    value={formik.values.presidente}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.presidente && Boolean(formik.errors.presidente)}
                                    helperText={formik.touched.presidente && formik.errors.presidente}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="CEP"
                                    name="cep"
                                    value={formik.values.cep}
                                    onChange={(e) => {
                                        formik.setFieldValue('cep', maskCEP(e.target.value));
                                    }}
                                    onBlur={(e) => {
                                        formik.handleBlur(e);
                                        handleCepBlur();
                                    }}
                                    error={formik.touched.cep && Boolean(formik.errors.cep)}
                                    helperText={formik.touched.cep && formik.errors.cep}
                                    InputProps={{
                                        endAdornment: loadingCep ? <CircularProgress size={20} /> : null
                                    }}
                                    inputProps={{ maxLength: 9 }}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} md={7}>
                                <TextField
                                    fullWidth
                                    label="Endereço"
                                    name="endereco"
                                    value={formik.values.endereco}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.endereco && Boolean(formik.errors.endereco)}
                                    helperText={formik.touched.endereco && formik.errors.endereco}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth error={formik.touched.uf && Boolean(formik.errors.uf)} required>
                                    <InputLabel>UF</InputLabel>
                                    <Select
                                        name="uf"
                                        value={formik.values.uf}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        label="UF"
                                    >
                                        <MenuItem value="">Selecione</MenuItem>
                                        {estadosBrasileiros.map(uf => (
                                            <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.uf && formik.errors.uf && (
                                        <FormHelperText>{formik.errors.uf}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Cidade"
                                    name="cidade"
                                    value={formik.values.cidade}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.cidade && Boolean(formik.errors.cidade)}
                                    helperText={formik.touched.cidade && formik.errors.cidade}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Telefone"
                                    name="telefone"
                                    value={formik.values.telefone}
                                    onChange={(e) => {
                                        formik.setFieldValue('telefone', maskPhone(e.target.value));
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.telefone && Boolean(formik.errors.telefone)}
                                    helperText={formik.touched.telefone && formik.errors.telefone}
                                    inputProps={{ maxLength: 15 }}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="WhatsApp"
                                    name="whatsapp"
                                    value={formik.values.whatsapp}
                                    onChange={(e) => {
                                        formik.setFieldValue('whatsapp', maskPhone(e.target.value));
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.whatsapp && Boolean(formik.errors.whatsapp)}
                                    helperText={formik.touched.whatsapp && formik.errors.whatsapp}
                                    inputProps={{ maxLength: 15 }}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="email"
                                    label="E-mail"
                                    name="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                    required
                                />
                            </Grid>
                        </Grid>

                        {/* CNPJs Relacionados */}
                        <Box sx={{ mt: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ color: '#1D385E' }}>
                                    Instituições Relacionadas
                                </Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={adicionarCnpjRelacionado}
                                    variant="outlined"
                                    size="small"
                                >
                                    Adicionar Instituição
                                </Button>
                            </Box>

                            {cnpjsRelacionados.map((relacionado, index) => (
                                <Paper key={relacionado.id} elevation={1} sx={{ mb: 2, p: 2, bgcolor: '#f9f9f9' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ color: '#1D385E', fontWeight: 600 }}>
                                            Instituição Relacionada {index + 1}
                                            {relacionado.nomeEmpresa && ` - ${relacionado.nomeEmpresa}`}
                                        </Typography>
                                        <Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleExpandRelacionado(relacionado.id)}
                                            >
                                                {expandedRelacionados[relacionado.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => removerCnpjRelacionado(relacionado.id)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Collapse in={expandedRelacionados[relacionado.id]}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={4}>
                                                <FormControl fullWidth size="small" error={!!relacionado.errors?.tipo}>
                                                    <InputLabel>Tipo de Instituição *</InputLabel>
                                                    <Select
                                                        value={relacionado.tipo}
                                                        onChange={(e) => atualizarCnpjRelacionado(relacionado.id, 'tipo', e.target.value)}
                                                        label="Tipo de Instituição *"
                                                    >
                                                        <MenuItem value="">Selecione</MenuItem>
                                                        {tiposRelacionamento.map(tipo => (
                                                            <MenuItem key={tipo.value} value={tipo.value}>
                                                                {tipo.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {relacionado.errors?.tipo && (
                                                        <FormHelperText>{relacionado.errors.tipo}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="CNPJ *"
                                                    value={relacionado.cnpj}
                                                    onChange={(e) => {
                                                        const maskedValue = maskCNPJ(e.target.value);
                                                        atualizarCnpjRelacionado(relacionado.id, 'cnpj', maskedValue);

                                                        if (validateBr.cnpj(maskedValue)) {
                                                            buscarDadosCNPJ(maskedValue, relacionado.id);
                                                        }
                                                    }}
                                                    InputProps={{
                                                        endAdornment: relacionado.loading ? <CircularProgress size={20} /> : null
                                                    }}
                                                    inputProps={{ maxLength: 18 }}
                                                    size="small"
                                                    disabled={!relacionado.tipo}
                                                    error={!!relacionado.errors?.cnpj}
                                                    helperText={relacionado.errors?.cnpj || (!relacionado.tipo ? "Selecione o tipo primeiro" : "")}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Nome da Empresa *"
                                                    value={relacionado.nomeEmpresa}
                                                    onChange={(e) => atualizarCnpjRelacionado(relacionado.id, 'nomeEmpresa', e.target.value)}
                                                    size="small"
                                                    error={!!relacionado.errors?.nomeEmpresa}
                                                    helperText={relacionado.errors?.nomeEmpresa}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    fullWidth
                                                    label="CEP *"
                                                    value={relacionado.cep}
                                                    onChange={(e) => atualizarCnpjRelacionado(relacionado.id, 'cep', maskCEP(e.target.value))}
                                                    inputProps={{ maxLength: 9 }}
                                                    size="small"
                                                    error={!!relacionado.errors?.cep}
                                                    helperText={relacionado.errors?.cep}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Endereço *"
                                                    value={relacionado.endereco}
                                                    onChange={(e) => atualizarCnpjRelacionado(relacionado.id, 'endereco', e.target.value)}
                                                    size="small"
                                                    error={!!relacionado.errors?.endereco}
                                                    helperText={relacionado.errors?.endereco}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <FormControl
                                                    fullWidth
                                                    size="small"
                                                    error={!!relacionado.errors?.uf || (relacionado.uf && relacionado.uf !== formik.values.uf)}
                                                    disabled={relacionado.ufPreenchidoAutomaticamente}
                                                >
                                                    <InputLabel>UF *</InputLabel>
                                                    <Select
                                                        value={relacionado.uf}
                                                        onChange={(e) => atualizarCnpjRelacionado(relacionado.id, 'uf', e.target.value)}
                                                        label="UF *"
                                                    >
                                                        <MenuItem value="">Selecione</MenuItem>
                                                        {estadosBrasileiros.map(uf => (
                                                            <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                                                        ))}
                                                    </Select>
                                                    {relacionado.errors?.uf ? (
                                                        <FormHelperText>{relacionado.errors.uf}</FormHelperText>
                                                    ) : relacionado.uf && relacionado.uf !== formik.values.uf ? (
                                                        <FormHelperText>
                                                            Deve ser {formik.values.uf || 'igual ao UF principal'}
                                                        </FormHelperText>
                                                    ) : relacionado.ufPreenchidoAutomaticamente ? (
                                                        <FormHelperText>
                                                            Preenchido automaticamente pela API
                                                        </FormHelperText>
                                                    ) : null}
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Cidade *"
                                                    value={relacionado.cidade}
                                                    onChange={(e) => atualizarCnpjRelacionado(relacionado.id, 'cidade', e.target.value)}
                                                    size="small"
                                                    error={!!relacionado.errors?.cidade}
                                                    helperText={relacionado.errors?.cidade}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Telefone *"
                                                    value={relacionado.telefone}
                                                    onChange={(e) => atualizarCnpjRelacionado(relacionado.id, 'telefone', maskPhone(e.target.value))}
                                                    inputProps={{ maxLength: 15 }}
                                                    size="small"
                                                    error={!!relacionado.errors?.telefone}
                                                    helperText={relacionado.errors?.telefone}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    type="email"
                                                    label="E-mail *"
                                                    value={relacionado.email}
                                                    onChange={(e) => atualizarCnpjRelacionado(relacionado.id, 'email', e.target.value)}
                                                    size="small"
                                                    error={!!relacionado.errors?.email}
                                                    helperText={relacionado.errors?.email}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Collapse>
                                </Paper>
                            ))}
                        </Box>

                        {/* Tipo de RPPS */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: '#1D385E' }}>
                            Tipo de RPPS
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl
                                    fullWidth
                                    error={formik.touched.tipoRPPS && Boolean(formik.errors.tipoRPPS)}
                                    required
                                    disabled={!formik.values.uf}
                                >
                                    <InputLabel>Tipo de RPPS</InputLabel>
                                    <Select
                                        name="tipoRPPS"
                                        value={formik.values.tipoRPPS}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        label="Tipo de RPPS"
                                    >
                                        <MenuItem value="">Selecione</MenuItem>
                                        {getTipoRPPSOptions().map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.tipoRPPS && formik.errors.tipoRPPS && (
                                        <FormHelperText>{formik.errors.tipoRPPS}</FormHelperText>
                                    )}
                                    {!formik.values.uf && (
                                        <FormHelperText>Selecione o UF primeiro</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* Tipo de Filiado */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: '#1D385E' }}>
                            Tipo de Filiado
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl
                                    fullWidth
                                    error={formik.touched.tipoFiliado && Boolean(formik.errors.tipoFiliado)}
                                    required
                                >
                                    <InputLabel>Selecione o tipo de filiado</InputLabel>
                                    <Select
                                        name="tipoFiliado"
                                        value={formik.values.tipoFiliado}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        label="Selecione o tipo de filiado"
                                    >
                                        <MenuItem value="">Selecione</MenuItem>
                                        {tiposFiliado.map(tipo => (
                                            <MenuItem key={tipo.id} value={tipo.id}>
                                                {tipo.descricao} - R$ {tipo.valor.toFixed(2)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.tipoFiliado && formik.errors.tipoFiliado && (
                                        <FormHelperText>{formik.errors.tipoFiliado}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            {formik.values.tipoFiliado && (
                                <Grid item xs={12}>
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            p: 3,
                                            bgcolor: '#f5f5f5',
                                            textAlign: 'center',
                                            border: '2px solid #1D385E'
                                        }}
                                    >
                                        <Typography variant="subtitle1" color="textSecondary">
                                            Valor Anual
                                        </Typography>
                                        <Typography variant="h4" sx={{ color: '#1D385E', fontWeight: 700, mt: 1 }}>
                                            R$ {getValorAnual().toFixed(2)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>

                        {/* Botões */}
                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => formik.resetForm()}
                                disabled={loading}
                            >
                                Limpar
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading || !formik.isValid || ufError !== ''}
                                sx={{
                                    bgcolor: '#1D385E',
                                    '&:hover': { bgcolor: '#152a47' }
                                }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Enviar Filiação'}
                            </Button>
                        </Box>
                    </form>}
                </Paper>
            </Box>

            {/* Modal de Segunda Via */}
            <Dialog
                open={openModalSegundaVia}
                onClose={handleCloseModalSegundaVia}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: '#1D385E', fontWeight: 600 }}>
                    {tipoSegundaVia === 'boleto' ? 'Segunda Via do Boleto' : 'Recibo de Pagamento'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
                        {tipoSegundaVia === 'boleto' ? 'Digite o CNPJ da empresa para buscar o boleto pendente de filiação.' : 'Digite o CNPJ da empresa para buscar o recibo de pagamento.'}
                    </Typography>
                    <TextField
                        fullWidth
                        label="CNPJ"
                        value={cnpjSegundaVia}
                        onChange={(e) => {
                            const maskedValue = maskCNPJ(e.target.value);
                            setCnpjSegundaVia(maskedValue);
                            if (errorCnpjSegundaVia) {
                                setErrorCnpjSegundaVia('');
                            }
                        }}
                        error={!!errorCnpjSegundaVia}
                        helperText={errorCnpjSegundaVia}
                        inputProps={{ maxLength: 18 }}
                        placeholder="00.000.000/0000-00"
                        autoFocus
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button
                        onClick={handleCloseModalSegundaVia}
                        disabled={loadingBoleto}
                        sx={{ color: '#666' }}
                    >
                        Cancelar
                    </Button>
                    {tipoSegundaVia === 'boleto' && (
                        <Button
                            onClick={buscarBoletoPendente}
                            variant="contained"
                            disabled={loadingBoleto || !cnpjSegundaVia}
                            sx={{
                                bgcolor: '#1D385E',
                                '&:hover': { bgcolor: '#152a47' }
                            }}
                        >
                            {loadingBoleto ? <CircularProgress size={24} /> : 'Buscar Boleto'}
                        </Button>)}
                    {tipoSegundaVia === 'recibo' && (
                        <Button
                            onClick={buscarReciboPagamento}
                            variant="contained"
                            disabled={loadingBoleto || !cnpjSegundaVia}
                            colo="success"
                        >
                            {loadingBoleto ? <CircularProgress size={24} /> : 'Buscar Recibo'}
                        </Button>)}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Affiliate;
